import { EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';

/**
 * services
 */
import { UploadWatcherService } from '../upload-watcher.service';

/**
 * providers
 */
import { provideUploads } from '../../provider';

/**
 * Registers the upload watcher for the whole app, together with the
 * uploads layers it reads through.
 *
 * These are environment providers and not plain ones — the opposite of
 * {@link provideUploads}, which is declared on the screen showing the
 * batches so its state dies with that screen. The watcher exists for the
 * one thing that screen cannot do: outlive itself. It is registered once,
 * at the root, and belongs to the session rather than to a route.
 *
 * It gets its own uploads facade out of the same call, so it never
 * depends on a screen having provided one. Two instances of a stateless
 * facade cost nothing.
 *
 * The watcher is created at start-up and not on first injection. Left to
 * the usual way, it would be built when a screen first asked to watch a
 * batch — which never comes for the batches a *previous* page left
 * running: they are taken back up in its constructor, and nobody is
 * going to ask for them. The one thing it costs is a constructor on a
 * page where nothing was uploaded, which wires an idle stream and makes
 * no request.
 *
 * @returns The providers to add.
 *
 * @example
 * ```typescript
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     provideUploadWatcher(),
 *   ],
 * };
 * ```
 */
export const provideUploadWatcher = (): EnvironmentProviders => {

    return makeEnvironmentProviders([

        ...provideUploads(),

        UploadWatcherService,

        provideAppInitializer(() => {
            inject(UploadWatcherService);
        }),
    ]);
};
